interface BarcodeResult {
  rawValue: string;
}

interface QrDetector {
  detect(source: HTMLVideoElement): Promise<BarcodeResult[]>;
}

type BarcodeDetectorConstructor = new (options: {
  formats: string[];
}) => QrDetector;

export function cameraAvailable(): boolean {
  // Camera permission must not depend on native QR support (absent in some
  // mobile browsers). The bundled decoder handles those browsers locally.
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

export function createQrDetector(): QrDetector {
  const NativeDetector = (
    globalThis as typeof globalThis & {
      BarcodeDetector?: BarcodeDetectorConstructor;
    }
  ).BarcodeDetector;
  let native: QrDetector | undefined;
  try {
    if (NativeDetector) native = new NativeDetector({ formats: ["qr_code"] });
  } catch {
    // Some browsers expose the API without supporting the QR format.
  }
  let canvas: HTMLCanvasElement | undefined;
  let decoder: Promise<typeof import("jsqr")> | undefined;

  return {
    async detect(video) {
      if (video.readyState < 2 || !video.videoWidth || !video.videoHeight)
        return [];
      if (native) {
        try {
          return await native.detect(video);
        } catch {
          native = undefined;
        }
      }
      decoder ??= import("jsqr");
      const { default: jsQR } = await decoder;
      canvas ??= document.createElement("canvas");
      // Bound per-frame work on phones while keeping enough QR detail.
      const scale = Math.min(
        1,
        960 / Math.max(video.videoWidth, video.videoHeight),
      );
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("QR canvas unavailable");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frame = context.getImageData(0, 0, canvas.width, canvas.height);
      const result = jsQR(frame.data, canvas.width, canvas.height);
      return result ? [{ rawValue: result.data }] : [];
    },
  };
}
